import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { UNIVERSITIES } from "../data/universities";

export default function Universita() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [hash]);

  return (
    <div data-testid="page-universita">
      <PageHero
        eyebrow="Le 3 università telematiche"
        title="Un Polo, tre atenei MIUR."
        sub="Scegli l'università più adatta al tuo percorso. Iscrizione, esami e tutoraggio gestiti tutti in Via Forlanini 18."
      />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 space-y-24">
        {UNIVERSITIES.map((u, idx) => (
          <section key={u.id} id={u.id} className="scroll-mt-28" data-testid={`uni-section-${u.id}`}>
            <div className={`grid lg:grid-cols-12 gap-10 items-start ${idx % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
              <div className="lg:col-span-5 lg:[direction:ltr]">
                <div className="relative rounded-3xl overflow-hidden h-72 lg:h-96 bg-white border border-brand-line flex items-center justify-center p-12">
                  <div className="absolute inset-0 opacity-[0.06]" style={{ background: `radial-gradient(circle at 50% 40%, ${u.color}, transparent 70%)` }} />
                  <img src={u.logo} alt={u.name} className="relative max-h-56 max-w-[85%] object-contain" />
                  <span className="absolute top-5 left-5 text-[10px] font-bold tracking-[0.2em] uppercase px-2.5 py-1 rounded-full" style={{ background: `${u.color}15`, color: u.color }}>
                    {u.badge}
                  </span>
                </div>
              </div>

              <div className="lg:col-span-7 lg:[direction:ltr]">
                <div className="eyebrow mb-2" style={{ color: u.color }}>{u.short} · {u.students} studenti in Italia</div>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-brand-navy leading-tight">
                  {u.name}
                </h2>
                <div className="mt-2 text-lg font-semibold" style={{ color: u.color }}>{u.tagline}</div>
                <p className="mt-4 text-brand-muted text-lg leading-relaxed">{u.description}</p>

                <div className="mt-8 grid sm:grid-cols-3 gap-4">
                  <CourseBox title="Triennali" items={u.triennali} testid={`${u.id}-triennali`} />
                  <CourseBox title="Magistrali" items={u.magistrali} testid={`${u.id}-magistrali`} />
                  <CourseBox title="Master" items={u.master} testid={`${u.id}-master`} />
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/contatti" state={{ uni: u.id }} className="btn-primary" data-testid={`${u.id}-cta-iscriviti`}>
                    Scopri i corsi di {u.short} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/orientamento" className="btn-secondary" data-testid={`${u.id}-cta-orientamento`}>
                    Parla con un tutor
                  </Link>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function CourseBox({ title, items, testid }) {
  return (
    <div className="rounded-2xl border border-brand-line bg-white p-5" data-testid={testid}>
      <div className="eyebrow text-brand-emerald mb-3">{title}</div>
      <ul className="space-y-2 text-sm">
        {items.slice(0, 5).map((c) => (
          <li key={c} className="flex gap-2 text-brand-ink">
            <CheckCircle2 className="w-4 h-4 text-brand-emerald shrink-0 mt-0.5" />
            <span>{c}</span>
          </li>
        ))}
        {items.length > 5 && (
          <li className="text-xs text-brand-muted font-semibold pl-6">+ altri {items.length - 5}</li>
        )}
      </ul>
    </div>
  );
}

export function PageHero({ eyebrow, title, sub }) {
  return (
    <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-brand-cream overflow-hidden border-b border-brand-line">
      <div className="absolute inset-0 dot-pattern opacity-[0.08]" />
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-brand-emerald/8 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-brand-gold/8 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl">
          <div className="eyebrow text-brand-emerald mb-3">{eyebrow}</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-navy">{title}</h1>
          {sub && <p className="mt-5 text-brand-muted text-lg leading-relaxed max-w-2xl">{sub}</p>}
        </div>
      </div>
    </section>
  );
}
